import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import Device from '../components/Device';
import EnterpriseDeviceCluster from '../components/EnterpriseDeviceCluster';
import '../styles/device-management.css';
import * as deviceManagementApi from '../services/deviceManagementApi';
import * as signatureService from '../services/signatureService';

/**
 * Device Management Page
 * Displays details for a specific device based on the order ID
 */
const DeviceManagementPage = () => {
  const { orderId } = useParams(); // Using orderId from URL
  const { wallet } = useWallet();
  const navigate = useNavigate();
  
  // Main data states
  const [orderData, setOrderData] = useState(null);
  const [claimStatus, setClaimStatus] = useState(null);
  const [physicalDevices, setPhysicalDevices] = useState([]);
  const [shippingStatus, setShippingStatus] = useState(null);
  
  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingClaimStatus, setIsLoadingClaimStatus] = useState(false);
  const [isLoadingPhysicalDevices, setIsLoadingPhysicalDevices] = useState(false);
  const [isLoadingShippingStatus, setIsLoadingShippingStatus] = useState(false);
  const [error, setError] = useState(null);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Device version selection states - initialize to 0, will set to full amount when form opens
  const [mobileVersion, setMobileVersion] = useState(0);
  const [homeVersion, setHomeVersion] = useState(0);
  
  // Shipping form state
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States',
    phoneNumber: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // First effect - Check claim status when wallet is connected
  useEffect(() => {
    if (wallet.connected && orderId) {
      checkClaimStatus();
    } else {
      setIsLoading(false);
    }
  }, [wallet, orderId]);
  
  // Second effect - If there are claimed devices, fetch shipping status
  useEffect(() => {
    if (claimStatus && claimStatus.is_claimed && claimStatus.claimed_devices?.length > 0) {
      fetchShippingStatus();
      
      // If any device has been delivered, fetch physical devices
      const hasDeliveredDevices = claimStatus.claimed_devices.some(
        device => device.shipping_status?.status === 'delivered'
      );
      
      if (hasDeliveredDevices) {
        fetchPhysicalDevices();
      }
    }
  }, [claimStatus]);
  
  // Effect to validate device version selection
  useEffect(() => {
    if (claimStatus && claimStatus.available_claim_count) {
      const totalSelected = mobileVersion + homeVersion;
      
      // If selected more than available, adjust
      if (totalSelected > claimStatus.available_claim_count) {
        // Priority to adjust home version first
        if (homeVersion > 0) {
          setHomeVersion(Math.max(0, homeVersion - (totalSelected - claimStatus.available_claim_count)));
        } else if (mobileVersion > 0) {
          setMobileVersion(Math.max(0, claimStatus.available_claim_count));
        }
      }
    }
  }, [mobileVersion, homeVersion, claimStatus]);
  
  // Check if the order has any claimable devices - FIRST API CALL
  const checkClaimStatus = async () => {
    setIsLoading(true);
    setIsLoadingClaimStatus(true);
    setError(null);
    
    try {
      const response = await deviceManagementApi.getDeviceClaimStatus(orderId, wallet);
      
      if (response.success && response.data) {
        setClaimStatus(response.data);
        
        // Save order data extracted from claim status
        setOrderData({
          type: response.data.order_type || 'unknown',
          value: response.data.order_value || 0
        });
      } else {
        throw new Error(response.message || 'Failed to check claim status');
      }
    } catch (error) {
      console.error('Error checking claim status:', error);
      setError(`Failed to check order status: ${error.message}`);
      
      // Clear signature cache if we get an auth error
      if (error.message?.includes('signature') || error.message?.includes('auth')) {
        signatureService.clearSignatureCache();
      }
    } finally {
      setIsLoadingClaimStatus(false);
      setIsLoading(false);
    }
  };
  
  // Fetch shipping status if devices are claimed
  const fetchShippingStatus = async () => {
    setIsLoadingShippingStatus(true);
    
    try {
      const response = await deviceManagementApi.getShippingStatus(orderId, wallet);
      
      if (response.success && response.data) {
        setShippingStatus(response.data.shipping_order);
      }
    } catch (error) {
      console.error('Error fetching shipping status:', error);
    } finally {
      setIsLoadingShippingStatus(false);
    }
  };
  
  // Fetch physical devices for delivered orders
  const fetchPhysicalDevices = async () => {
    setIsLoadingPhysicalDevices(true);
    
    try {
      const response = await deviceManagementApi.getPhysicalDevices(orderId, wallet);
      
      if (response.success && response.data) {
        setPhysicalDevices(response.data.physical_devices || []);
      }
    } catch (error) {
      console.error('Error fetching physical devices:', error);
    } finally {
      setIsLoadingPhysicalDevices(false);
    }
  };
  
  // Handle input change in shipping form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Handle device version change
  const handleDeviceVersionChange = (type, value) => {
    const numValue = parseInt(value, 10) || 0;
    
    if (type === 'mobile') {
      setMobileVersion(numValue);
    } else if (type === 'home') {
      setHomeVersion(numValue);
    }
  };
  
  // Validate shipping form
  const validateForm = () => {
    const errors = {};
    
    if (!shippingInfo.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!shippingInfo.streetAddress.trim()) {
      errors.streetAddress = 'Street address is required';
    }
    
    if (!shippingInfo.city.trim()) {
      errors.city = 'City is required';
    }
    
    if (!shippingInfo.state.trim()) {
      errors.state = 'State/Province is required';
    }
    
    if (!shippingInfo.postalCode.trim()) {
      errors.postalCode = 'Postal/ZIP code is required';
    }
    
    if (!shippingInfo.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    } else {
      // Basic phone number validation
      const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
      if (!phoneRegex.test(shippingInfo.phoneNumber)) {
        errors.phoneNumber = 'Please enter a valid phone number';
      }
    }
    
    // Validate device version selection
    const totalSelected = mobileVersion + homeVersion;
    if (totalSelected === 0) {
      errors.deviceVersion = 'Please select at least one device version';
    } else if (totalSelected > claimStatus.available_claim_count) {
      errors.deviceVersion = `You can only select up to ${claimStatus.available_claim_count} devices in total`;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Add device version selections to street address
    const streetAddressWithVersions = `${shippingInfo.streetAddress} [Mobile: ${mobileVersion}, Home: ${homeVersion}]`;
    
    try {
      // Create a modified shipping info object with the updated street address
      const modifiedShippingInfo = {
        ...shippingInfo,
        streetAddress: streetAddressWithVersions
      };
      
      const response = await deviceManagementApi.claimDevice(orderId, wallet, modifiedShippingInfo);
      
      if (response.success) {
        setSubmitSuccess(true);
        setShowClaimForm(false);
        
        // Update claim status to show all devices are now claimed
        if (response.data) {
          const shippingOrderData = response.data.shipping_order;
          
          // Add newly claimed device to the list
          const updatedClaimStatus = {
            ...claimStatus,
            is_claimed: true,
            available_claim_count: 0, // Set to 0 since all devices are now claimed
            claimed_devices: [
              ...(claimStatus.claimed_devices || []),
              {
                device_serial: response.data.claim_id || 'Pending Assignment',
                claim_date: response.data.claim_date,
                shipping_status: {
                  status: shippingOrderData.status,
                  order_number: shippingOrderData.order_number,
                  estimated_ship_date: shippingOrderData.estimated_ship_date
                }
              }
            ]
          };
          
          setClaimStatus(updatedClaimStatus);
          
          // Reset version selections
          setMobileVersion(0);
          setHomeVersion(0);
          
          // Fetch updated shipping status
          fetchShippingStatus();
        }
      } else {
        throw new Error(response.message || 'Failed to claim device');
      }
    } catch (error) {
      console.error('Error claiming device:', error);
      setFormErrors({
        submit: `Failed to claim device: ${error.message}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format currency for display
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  // Get device information based on type
  const getDeviceInfo = (type) => {
    const deviceInfo = {
      basic: {
        name: 'AeroNode Basic'
      },
      pro: {
        name: 'AeroNode Pro'
      },
      elite: {
        name: 'AeroNode Elite'
      },
      enterprise: {
        name: 'AeroNode Enterprise'
      }
    };
    
    return deviceInfo[type] || deviceInfo.basic;
  };
  
  // Handle refresh button click
  const handleRefresh = () => {
    signatureService.clearSignatureCache();
    checkClaimStatus();
  };
  
  // Render wallet disconnected state
  const renderWalletDisconnected = () => (
    <div className="device-empty-state">
      <div className="empty-state-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 7L12 3L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4 7V17L12 21L20 17V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h3>Wallet Not Connected</h3>
      <p>Connect your wallet to view device details.</p>
      <button className="btn" onClick={() => wallet.openWalletModal && wallet.openWalletModal()}>Connect Wallet</button>
    </div>
  );
  
  // Render claim form
  const renderClaimForm = () => (
    <div className="claim-form-container">
      <h3>Ship My Device</h3>
      <p>Please enter your shipping information to claim your physical device.</p>
      
      <div className="shipping-notice">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <div>
          <p>Normal delivery takes 7 business days. Special circumstances may require up to 15 days.</p>
          <p>Shipping costs are collected on delivery (cash on delivery).</p>
        </div>
      </div>
      
      {formErrors.submit && (
        <div className="form-error">{formErrors.submit}</div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="device-version-selection">
          <h4>Select Device Versions</h4>
          <p>Choose how many of each version you want (total: {mobileVersion + homeVersion}/{claimStatus?.available_claim_count || 0})</p>
          
          <div className="version-selectors">
            <div className="version-selector">
              <label htmlFor="mobileVersion">Mobile Version</label>
              <div className="counter-input">
                <button 
                  type="button" 
                  className="counter-btn" 
                  onClick={() => handleDeviceVersionChange('mobile', Math.max(0, mobileVersion - 1))}
                  disabled={mobileVersion <= 0}
                  aria-label="Decrease mobile version count"
                >-</button>
                <input
                  type="number"
                  id="mobileVersion"
                  min="0"
                  max={claimStatus?.available_claim_count || 0}
                  value={mobileVersion}
                  onChange={(e) => handleDeviceVersionChange('mobile', e.target.value)}
                  aria-label="Mobile version count"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <button 
                  type="button" 
                  className="counter-btn" 
                  onClick={() => handleDeviceVersionChange('mobile', mobileVersion + 1)}
                  disabled={mobileVersion + homeVersion >= (claimStatus?.available_claim_count || 0)}
                  aria-label="Increase mobile version count"
                >+</button>
              </div>
            </div>
            
            <div className="version-selector">
              <label htmlFor="homeVersion">Home Version</label>
              <div className="counter-input">
                <button 
                  type="button" 
                  className="counter-btn" 
                  onClick={() => handleDeviceVersionChange('home', Math.max(0, homeVersion - 1))}
                  disabled={homeVersion <= 0}
                  aria-label="Decrease home version count"
                >-</button>
                <input
                  type="number"
                  id="homeVersion"
                  min="0"
                  max={claimStatus?.available_claim_count || 0}
                  value={homeVersion}
                  onChange={(e) => handleDeviceVersionChange('home', e.target.value)}
                  aria-label="Home version count"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <button 
                  type="button" 
                  className="counter-btn" 
                  onClick={() => handleDeviceVersionChange('home', homeVersion + 1)}
                  disabled={mobileVersion + homeVersion >= (claimStatus?.available_claim_count || 0)}
                  aria-label="Increase home version count"
                >+</button>
              </div>
            </div>
          </div>
          
          {formErrors.deviceVersion && (
            <div className="field-error device-version-error">{formErrors.deviceVersion}</div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="fullName">Full Name</label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={shippingInfo.fullName}
            onChange={handleInputChange}
            placeholder="John Doe"
            className={formErrors.fullName ? 'error' : ''}
            autoComplete="name"
          />
          {formErrors.fullName && <div className="field-error">{formErrors.fullName}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="streetAddress">Street Address</label>
          <input
            type="text"
            id="streetAddress"
            name="streetAddress"
            value={shippingInfo.streetAddress}
            onChange={handleInputChange}
            placeholder="123 Main St, Apt 4B"
            className={formErrors.streetAddress ? 'error' : ''}
            autoComplete="street-address"
          />
          {formErrors.streetAddress && <div className="field-error">{formErrors.streetAddress}</div>}
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              name="city"
              value={shippingInfo.city}
              onChange={handleInputChange}
              placeholder="New York"
              className={formErrors.city ? 'error' : ''}
              autoComplete="address-level2"
            />
            {formErrors.city && <div className="field-error">{formErrors.city}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="state">State/Province</label>
            <input
              type="text"
              id="state"
              name="state"
              value={shippingInfo.state}
              onChange={handleInputChange}
              placeholder="New York"
              className={formErrors.state ? 'error' : ''}
              autoComplete="address-level1"
            />
            {formErrors.state && <div className="field-error">{formErrors.state}</div>}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="postalCode">Postal/ZIP Code</label>
            <input
              type="text"
              id="postalCode"
              name="postalCode"
              value={shippingInfo.postalCode}
              onChange={handleInputChange}
              placeholder="100000"
              className={formErrors.postalCode ? 'error' : ''}
              autoComplete="postal-code"
              inputMode="numeric"
              pattern="[0-9]*"
            />
            {formErrors.postalCode && <div className="field-error">{formErrors.postalCode}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="country">Country</label>
            <div className="custom-select-wrapper">
              <select
                id="country"
                name="country"
                value={shippingInfo.country}
                onChange={handleInputChange}
                className="custom-select"
                autoComplete="country"
              >
                <option value="United States">United States</option>
                <option value="China">China</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Japan">Japan</option>
                <option value="Singapore">Singapore</option>
                <option value="South Korea">South Korea</option>
                <option value="Other">Other</option>
              </select>
              <div className="select-arrow">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={shippingInfo.phoneNumber}
            onChange={handleInputChange}
            placeholder="+1 000 000 1234"
            className={formErrors.phoneNumber ? 'error' : ''}
            autoComplete="tel"
            inputMode="tel"
          />
          {formErrors.phoneNumber && <div className="field-error">{formErrors.phoneNumber}</div>}
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={() => setShowClaimForm(false)}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Order'}
          </button>
        </div>
      </form>
    </div>
  );
  
  // Render shipping status information
  const renderShippingStatus = () => {
    if (isLoadingShippingStatus) {
      return (
        <div className="loading-placeholder">
          <div className="spinner small"></div>
          <span>Loading shipping status...</span>
        </div>
      );
    }
    
    if (!shippingStatus) {
      return (
        <div className="info-message">
          <span>Shipping information is being processed.</span>
        </div>
      );
    }
    
    return (
      <div className="order-status-container">
        <h3>Shipping Status</h3>
        
        <div className="shipping-tracker">
          <div className={`tracker-step ${shippingStatus.status === 'processing' || shippingStatus.status === 'shipped' || shippingStatus.status === 'delivered' ? 'active' : ''}`}>
            <div className="step-indicator">1</div>
            <div className="step-label">Processing</div>
          </div>
          <div className="tracker-line"></div>
          <div className={`tracker-step ${shippingStatus.status === 'shipped' || shippingStatus.status === 'delivered' ? 'active' : ''}`}>
            <div className="step-indicator">2</div>
            <div className="step-label">Shipped</div>
          </div>
          <div className="tracker-line"></div>
          <div className={`tracker-step ${shippingStatus.status === 'delivered' ? 'active' : ''}`}>
            <div className="step-indicator">3</div>
            <div className="step-label">Delivered</div>
          </div>
        </div>
        
        <div className="status-info">
          <div className="status-row">
            <div className="status-label">Status</div>
            <div className="status-value">
              <span className={`status-badge ${shippingStatus.status}`}>
                {shippingStatus.status.charAt(0).toUpperCase() + shippingStatus.status.slice(1)}
              </span>
            </div>
          </div>
          
          <div className="status-row">
            <div className="status-label">Order Number</div>
            <div className="status-value">{shippingStatus.order_number || 'N/A'}</div>
          </div>
          
          {shippingStatus.tracking_number && (
            <div className="status-row">
              <div className="status-label">Tracking Number</div>
              <div className="status-value">
                {shippingStatus.carrier && shippingStatus.tracking_number ? (
                  <a href={getTrackingUrl(shippingStatus.carrier, shippingStatus.tracking_number)} 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="tracking-link">
                    {shippingStatus.tracking_number}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                ) : (
                  shippingStatus.tracking_number
                )}
              </div>
            </div>
          )}
          
          {shippingStatus.carrier && (
            <div className="status-row">
              <div className="status-label">Carrier</div>
              <div className="status-value">{shippingStatus.carrier}</div>
            </div>
          )}
          
          {shippingStatus.estimated_delivery && (
            <div className="status-row">
              <div className="status-label">Estimated Delivery</div>
              <div className="status-value">{formatDate(shippingStatus.estimated_delivery)}</div>
            </div>
          )}
        </div>
        
        {shippingStatus.shipping_address && (
          <div className="shipping-address">
            <h4>Shipping Address</h4>
            <p>{shippingStatus.shipping_address.name}</p>
            <p>{shippingStatus.shipping_address.street}</p>
            <p>
              {shippingStatus.shipping_address.city}, {shippingStatus.shipping_address.state} {shippingStatus.shipping_address.postal_code}
            </p>
            <p>{shippingStatus.shipping_address.country}</p>
          </div>
        )}
        
        <div className="status-actions">
          <button className="btn-secondary" onClick={handleRefresh}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M23 4V10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1 20V14H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 9.00001C4.01717 7.56662 4.87913 6.2789 6.01547 5.27546C7.1518 4.27202 8.52547 3.58965 10.0083 3.29125C11.4911 2.99284 13.0348 3.09093 14.4761 3.57516C15.9175 4.05939 17.2119 4.90973 18.24 6.04001L23 10M1 14L5.76 17.96C6.78811 19.0903 8.08254 19.9406 9.52387 20.4249C10.9652 20.9091 12.5089 21.0072 13.9917 20.7088C15.4745 20.4104 16.8482 19.728 17.9845 18.7245C19.1209 17.7211 19.9828 16.4334 20.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Refresh Status
          </button>
        </div>
      </div>
    );
  };
  
  // Get tracking URL based on carrier
  const getTrackingUrl = (carrier, trackingNumber) => {
    const carrierMap = {
      'FedEx': `https://www.fedex.com/apps/fedextrack/?tracknumbers=${trackingNumber}`,
      'UPS': `https://www.ups.com/track?tracknum=${trackingNumber}`,
      'USPS': `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`,
      'DHL': `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}`,
      'SF Express': `https://www.sf-express.com/cn/en/dynamic_function/waybill/#search/bill-number/${trackingNumber}`,
      'China Post': `https://www.17track.net/en/track?nums=${trackingNumber}`
    };
    
    return carrierMap[carrier] || '#';
  };
  
  // Render physical devices
  const renderPhysicalDevices = () => {
    if (isLoadingPhysicalDevices) {
      return (
        <div className="loading-placeholder">
          <div className="spinner small"></div>
          <span>Loading physical devices...</span>
        </div>
      );
    }
    
    if (!physicalDevices || physicalDevices.length === 0) {
      if (shippingStatus && shippingStatus.status === 'delivered') {
        return (
          <div className="info-message">
            <span>Your device has been delivered. Physical device information will be updated soon.</span>
          </div>
        );
      }
      
      return null;
    }
    
    return (
      <div className="physical-devices-container">
        <h3>Your Physical Devices</h3>
        
        <div className="physical-devices-list">
          {physicalDevices.map((device, index) => (
            <div className="physical-device-card" key={index}>
              <div className="device-header">
                <div className="device-model">{device.model}</div>
                <div className={`device-status ${device.status}`}>{device.status}</div>
              </div>
              
              <div className="device-serial">
                <div className="serial-label">Serial Number</div>
                <div className="serial-value">{device.serial_number}</div>
              </div>
              
              <div className="device-details">
                <div className="detail-item">
                  <div className="detail-label">Activated</div>
                  <div className="detail-value">{formatDate(device.activation_date)}</div>
                </div>
                
                <div className="detail-item">
                  <div className="detail-label">Warranty Until</div>
                  <div className="detail-value">{formatDate(device.warranty_end_date)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Render claimed devices list
  const renderClaimedDevices = () => {
    if (!claimStatus || !claimStatus.claimed_devices || claimStatus.claimed_devices.length === 0) {
      return null;
    }
    
    return (
      <div className="claimed-devices-container">
        <h3>Claimed Devices</h3>
        
        <div className="claimed-devices-list">
          {claimStatus.claimed_devices.map((device, index) => (
            <div className="claimed-device-item" key={index}>
              <div className="device-header">
                <div className="device-serial">ID: {device.device_serial}</div>
                <div className="claim-date">Claimed: {formatDate(device.claim_date)}</div>
              </div>
              
              <div className="shipping-status">
                {device.shipping_status ? (
                  <div className={`status-badge ${device.shipping_status.status}`}>
                    {device.shipping_status.status.charAt(0).toUpperCase() + device.shipping_status.status.slice(1)}
                  </div>
                ) : (
                  <div className="status-badge processing">Processing</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Render the order info header
  const renderOrderHeader = () => {
    if (!orderData) return null;
    
    const deviceInfo = getDeviceInfo(orderData.type);
    
    return (
      <div className="order-info-header">
        <h2>{deviceInfo.name}</h2>
        <div className="order-meta">
          <div className="order-id">Order ID: #{orderId}</div>
          <div className="order-value">Value: {formatCurrency(orderData.value)}</div>
        </div>
      </div>
    );
  };
  
  // Main render for order content
  const renderOrderContent = () => {
    // If we're still loading the initial claim status
    if (isLoadingClaimStatus) {
      return (
        <div className="loading-section">
          <div className="spinner"></div>
          <p>Checking order status...</p>
        </div>
      );
    }
    
    // If there was an error loading the claim status
    if (!claimStatus) {
      return (
        <div className="error-section">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <p>Failed to load order claim status. Please try again.</p>
          <button className="btn" onClick={checkClaimStatus}>Retry</button>
        </div>
      );
    }
    
    return (
      <>
        {/* Order header with device type info */}
        {renderOrderHeader()}
        
        {/* Show claim status card */}
        <div className="claim-status-card">
          <div className="card-header">
            <h3>Device Claim Status</h3>
            <button className="refresh-btn" onClick={handleRefresh} title="Refresh Status">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M23 4V10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M1 20V14H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3.51 9.00001C4.01717 7.56662 4.87913 6.2789 6.01547 5.27546C7.1518 4.27202 8.52547 3.58965 10.0083 3.29125C11.4911 2.99284 13.0348 3.09093 14.4761 3.57516C15.9175 4.05939 17.2119 4.90973 18.24 6.04001L23 10M1 14L5.76 17.96C6.78811 19.0903 8.08254 19.9406 9.52387 20.4249C10.9652 20.9091 12.5089 21.0072 13.9917 20.7088C15.4745 20.4104 16.8482 19.728 17.9845 18.7245C19.1209 17.7211 19.9828 16.4334 20.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          
          {/* Only show the claim button/form when there are available devices AND not already submitted */}
          {claimStatus.available_claim_count > 0 && !submitSuccess && (
            <div className="available-claims">
              <div className="claim-count">
                <span className="count-value">{claimStatus.available_claim_count}</span>
                <span className="count-label">Available Device{claimStatus.available_claim_count !== 1 ? 's' : ''}</span>
              </div>
              
              {submitSuccess && (
                <div className="success-message">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1952 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Your device has been successfully claimed! We'll ship it to you soon.</span>
                </div>
              )}
              
              {showClaimForm ? (
                renderClaimForm()
              ) : (
                <button 
                  className="btn claim-btn" 
                  onClick={() => {
                    setShowClaimForm(true);
                    // Default to claiming all devices as mobile version
                    setMobileVersion(claimStatus.available_claim_count);
                    setHomeVersion(0);
                  }}
                >
                  Claim Physical Device
                </button>
              )}
            </div>
          )}
          
          {/* Show success message if submission was successful */}
          {submitSuccess && (
            <div className="success-message">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1952 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Your device has been successfully claimed! We'll ship it to you soon.</span>
            </div>
          )}
          
          {/* Show claimed devices list */}
          {renderClaimedDevices()}
          
          {/* Show shipping status */}
          {claimStatus.is_claimed && renderShippingStatus()}
          
          {/* Show physical devices if delivered */}
          {renderPhysicalDevices()}
        </div>
      </>
    );
  };
  
  return (
    <div className="device-management-page">
      <div className="container">
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate('/portfolio')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Portfolio
          </button>
          
          <h1>Device Management</h1>
        </div>
        
        {!wallet.connected ? (
          renderWalletDisconnected()
        ) : isLoading ? (
          <div className="device-loading">
            <div className="spinner"></div>
            <p>Loading order information...</p>
          </div>
        ) : error ? (
          <div className="device-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{error}</p>
            <button className="btn" onClick={checkClaimStatus}>Try Again</button>
            <button className="btn-secondary" onClick={() => navigate('/portfolio')}>Back to Portfolio</button>
          </div>
        ) : (
          <div className="device-management-content">
            {renderOrderContent()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceManagementPage;
